import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { createColumnHelper, useReactTable, getCoreRowModel, getFilteredRowModel, getSortedRowModel, FilterFn, SortingState } from '@tanstack/react-table';
import { useAuth } from '../AuthContext';
import { ALL_WORK_ORDERS_QUERY, UPDATE_WORK_ORDER_STATUS_MUTATION } from '../graphql/workOrders';
import Table from '../components/Table';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import './Quotes.css';

const STATUS_OPTIONS = ['ready', 'in_progress', 'on_hold', 'complete'];

interface WorkOrder {
  id: number;
  description: string;
  travelerNumber: string | null;
  status: string;
  createdAt: string;
  dueAt: string | null;
  userByAssignedTo: { id: number; username: string } | null;
  quoteByQuoteId: {
    id: number;
    name: string;
    jobByJobId: { id: number; name: string; customerByCustomerId: { id: number; name: string } };
  };
}

const statusColorMap: Record<string, { bg: string; text: string }> = {
  ready: { bg: '#f3f4f6', text: '#374151' },
  in_progress: { bg: '#dbeafe', text: '#1e40af' },
  on_hold: { bg: '#fef3c7', text: '#92400e' },
  complete: { bg: '#d1fae5', text: '#065f46' },
};

const filterFn: FilterFn<WorkOrder> = (row, _columnId, filterValue) => {
  if (!filterValue) return true;
  const s = String(filterValue).toLowerCase();
  const wo = row.original;
  return (
    wo.description.toLowerCase().includes(s) ||
    (wo.travelerNumber?.toLowerCase().includes(s) ?? false) ||
    wo.status.toLowerCase().includes(s) ||
    wo.quoteByQuoteId.name.toLowerCase().includes(s) ||
    wo.quoteByQuoteId.jobByJobId.customerByCustomerId.name.toLowerCase().includes(s) ||
    (wo.userByAssignedTo?.username.toLowerCase().includes(s) ?? false)
  );
};

const columnHelper = createColumnHelper<WorkOrder>();

export default function WorkOrders() {
  const { user } = useAuth();
  const isWorker = user?.role === 'worker';
  const navigate = useNavigate();
  const [globalFilter, setGlobalFilter] = useState('');
  const [jobFilter, setJobFilter] = useState('all');
  const [quoteFilter, setQuoteFilter] = useState('all');
  const [sorting, setSorting] = useState<SortingState>([{ id: 'createdAt', desc: true }]);
  const [updatingId, setUpdatingId] = useState<number | null>(null);

  const { data, loading, error, refetch } = useQuery(ALL_WORK_ORDERS_QUERY, {
    variables: { domainId: user?.domain_id || 0 },
    skip: !user?.domain_id,
    fetchPolicy: 'network-only',
  });
  const [updateStatus] = useMutation(UPDATE_WORK_ORDER_STATUS_MUTATION);

  const allWorkOrders: WorkOrder[] = data?.allWorkOrders?.nodes || [];
  // Also needs a stable reference for the same reason as `workOrders` below
  // — for worker accounts this used to be a fresh .filter() array every
  // render, which alone was enough to keep the table's data prop unstable.
  const roleScoped = useMemo(
    () => (isWorker ? allWorkOrders.filter((wo) => wo.userByAssignedTo?.id === user?.id) : allWorkOrders),
    [allWorkOrders, isWorker, user?.id]
  );

  // Job/Quote filters work together — pick a job, then only that job's
  // quotes show, same as the Shop Status board's filters. Plain arrays
  // rebuilt each render — cheap for this list size.
  const jobsById = new Map<number, string>();
  roleScoped.forEach((wo) => jobsById.set(wo.quoteByQuoteId.jobByJobId.id, wo.quoteByQuoteId.jobByJobId.name));
  const jobs = Array.from(jobsById, ([id, name]) => ({ id, name }));

  const quotesById = new Map<number, { name: string; jobId: number }>();
  roleScoped.forEach((wo) => quotesById.set(wo.quoteByQuoteId.id, { name: wo.quoteByQuoteId.name, jobId: wo.quoteByQuoteId.jobByJobId.id }));
  const quotes = Array.from(quotesById, ([id, v]) => ({ id, name: v.name, jobId: v.jobId }))
    .filter((q) => jobFilter === 'all' || q.jobId === Number(jobFilter));

  const handleJobFilterChange = (value: string) => {
    setJobFilter(value);
    setQuoteFilter('all');
  };

  // useReactTable's `data` option must be a stable reference when nothing
  // relevant changed, or its internal auto-reset logic loops forever
  // reacting to what looks like new data on every render (documented
  // TanStack Table gotcha — not just a style preference).
  const workOrders = useMemo(
    () => roleScoped
      .filter((wo) => jobFilter === 'all' || wo.quoteByQuoteId.jobByJobId.id === Number(jobFilter))
      .filter((wo) => quoteFilter === 'all' || wo.quoteByQuoteId.id === Number(quoteFilter)),
    [roleScoped, jobFilter, quoteFilter]
  );

  const handleStatusChange = async (wo: WorkOrder, newStatus: string) => {
    if (newStatus === wo.status) return;
    setUpdatingId(wo.id);
    try {
      await updateStatus({ variables: { id: wo.id, status: newStatus } });
      await refetch();
    } finally {
      setUpdatingId(null);
    }
  };

  const columns = useMemo(
    () => [
      columnHelper.accessor('travelerNumber', {
        header: 'Traveler #',
        cell: (info) => info.getValue() || '—',
        size: 100,
      }),
      columnHelper.accessor('description', {
        header: 'Description',
        cell: (info) => (
          <span
            style={{ color: '#3b82f6', fontWeight: 600, cursor: 'pointer' }}
            onClick={() => navigate(`/work-orders/${info.row.original.id}`)}
          >
            {info.getValue()}
          </span>
        ),
        size: 240,
      }),
      columnHelper.accessor((row) => row.quoteByQuoteId.jobByJobId.customerByCustomerId.name, {
        id: 'customer',
        header: 'Customer',
        cell: (info) => info.getValue(),
        size: 160,
      }),
      columnHelper.accessor((row) => row.quoteByQuoteId.name, {
        id: 'quote',
        header: 'Quote',
        cell: (info) => (
          <span
            style={{ color: '#6b7280', cursor: 'pointer', textDecoration: 'underline', textDecorationStyle: 'dotted' }}
            onClick={(e) => { e.stopPropagation(); navigate(`/quotes/${info.row.original.quoteByQuoteId.id}`); }}
          >
            #{info.row.original.quoteByQuoteId.id} {info.getValue()}
          </span>
        ),
        size: 160,
      }),
      columnHelper.accessor((row) => row.userByAssignedTo?.username ?? null, {
        id: 'assignedTo',
        header: 'Assigned To',
        cell: (info) => info.getValue() || '—',
        size: 120,
      }),
      columnHelper.accessor('status', {
        header: 'Status',
        cell: (info) => {
          const wo = info.row.original;
          const status = info.getValue();
          const colors = statusColorMap[status] || statusColorMap.ready;
          if (!isWorker) {
            return (
              <span
                className="status-badge"
                style={{ backgroundColor: colors.bg, color: colors.text, textTransform: 'capitalize' }}
              >
                {status.replace('_', ' ')}
              </span>
            );
          }
          return (
            <select
              value={status}
              disabled={updatingId === wo.id}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => handleStatusChange(wo, e.target.value)}
              style={{
                padding: '3px 8px',
                border: `2px solid ${colors.text}`,
                borderRadius: 14,
                backgroundColor: colors.bg,
                color: colors.text,
                fontWeight: 700,
                fontSize: 12,
                cursor: 'pointer',
                textTransform: 'capitalize',
              }}
            >
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {opt.replace('_', ' ')}
                </option>
              ))}
            </select>
          );
        },
        size: 140,
      }),
      columnHelper.accessor('dueAt', {
        header: 'Install Date',
        cell: (info) => {
          const d = info.getValue();
          return d ? new Date(d).toLocaleDateString() : '—';
        },
        size: 110,
      }),
      columnHelper.accessor('createdAt', {
        header: 'Created',
        cell: (info) => new Date(info.getValue()).toLocaleDateString(),
        size: 100,
      }),
    ],
    [navigate, isWorker, updatingId]
  );

  const table = useReactTable({
    data: workOrders,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    globalFilterFn: filterFn,
    state: { globalFilter, sorting },
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
  });

  const hasNone = roleScoped.length === 0;
  const noFilterMatches = !hasNone && workOrders.length === 0;

  return (
    <div className="quotes-page">
      <div className="page-header">
        <div>
          <h1>{isWorker ? 'My Work Orders' : 'Work Orders'}</h1>
          <p className="subtitle">{isWorker ? 'Work orders assigned to you' : 'Track and manage shop traveler sheets'}</p>
        </div>
      </div>

      <div className="page-content">
        {error && (
          <div className="error-message">
            <strong>Error:</strong> {error.message}
          </div>
        )}

        {loading && hasNone && <div className="loading-message">Loading...</div>}

        {!loading && hasNone && (
          <div className="empty-state">
            <div className="empty-icon"><FolderOpenIcon fontSize="inherit" /></div>
            <h2>No work orders {isWorker ? 'assigned to you' : 'yet'}</h2>
            {!isWorker && <p>Open a quote and click "+ New Work Order" to create one</p>}
          </div>
        )}

        {!hasNone && (
          <>
            <div className="filter-bar" style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
              <input
                type="text"
                className="filter-input"
                placeholder="Search by description, customer, status, or traveler #..."
                value={globalFilter}
                onChange={(e) => setGlobalFilter(e.target.value)}
              />
              <select
                value={jobFilter}
                onChange={(e) => handleJobFilterChange(e.target.value)}
                style={{ padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, background: '#fff' }}
              >
                <option value="all">All Jobs</option>
                {jobs.map((j) => (
                  <option key={j.id} value={j.id}>{j.name}</option>
                ))}
              </select>
              <select
                value={quoteFilter}
                onChange={(e) => setQuoteFilter(e.target.value)}
                style={{ padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, background: '#fff' }}
              >
                <option value="all">All Quotes</option>
                {quotes.map((q) => (
                  <option key={q.id} value={q.id}>#{q.id} {q.name}</option>
                ))}
              </select>
              <select
                value={sorting[0]?.desc ? 'desc' : 'asc'}
                onChange={(e) => setSorting([{ id: 'createdAt', desc: e.target.value === 'desc' }])}
                style={{ padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, background: '#fff' }}
              >
                <option value="desc">Created — Newest First</option>
                <option value="asc">Created — Oldest First</option>
              </select>
            </div>
            {noFilterMatches ? (
              <div className="empty-state">
                <div className="empty-icon"><FolderOpenIcon fontSize="inherit" /></div>
                <h2>No work orders match these filters</h2>
              </div>
            ) : (
              <Table table={table} isLoading={loading} />
            )}
          </>
        )}
      </div>
    </div>
  );
}
